<template>
  <div class="custom-form py-4">
    <div class="pb-4">
      <h6 class="font-bold">Product Family</h6>
    </div>
    <vs-row vs-align="flex-start" vs-type="flex" vs-justify="left" vs-w="12">
      <vs-col vs-lg="4" vs-sm="8" vs-xs="12" class="mb-5">
        <v-select
          v-model="localProductFamilyId"
          :options="productFamilies"
          :reduce="item => item._id"
          label="name"
          placeholder="Select Product Family"
          class="w-full"
          :disabled="productFamilies.length === 0"
          :clearable="false"
          @input="handleProductFamilyChange"
        />
        <p v-if="productFamilies.length === 0" class="text-xs text-warning mb-3">
          Product family options are empty.
        </p>
        <p v-else-if="productFamilies.length > 0 && !productFamilies.some(r => r._id === localProductFamilyId)" class="text-xs text-warning mb-3">
          No product family selected or previous product family is deleted.
        </p>
        <vs-button
          type="line"
          class="p-0 pb-1 text-xs text-left"
          @click="productFamilyPopupHandler"
        >
          Manage Product Families
        </vs-button>
        <vs-popup class="product-family-popup" title="Manage Product Families" :active.sync="productFamilyPopupActivo">
          <div class="p-8 flex flex-col gap-5">
            <div>
              <h3>Manage Product Families</h3>
              <span class="text-base pt-3">Add, edit, or remove product family options</span>
            </div>
            <div class="flex flex-col lg:grid lg:grid-cols-3 gap-4">
              <div class="col-span-1 flex flex-col gap-2">
                <vs-input
                  label="Product Family Name"
                  class="w-full"
                  placeholder="e.g. Restylane, Juvederm, etc."
                  v-model="currentProductFamily.name"
                  :success="!!(!errors.first('productFamily.productFamilytxtbox') && currentProductFamily.name)"
                  val-icon-success="done"
                  val-icon-danger="error"
                  :danger="errors.first('productFamily.productFamilytxtbox') ? true : false"
                  v-validate="productFamilyPopupActivo ? 'required' : ''"
                  name="productFamilytxtbox"
                  ref="productFamilytxtbox"
                  :danger-text="errors.first('productFamily.productFamilytxtbox')"
                  data-vv-as="product family name"
                  data-vv-scope="productFamily"
                />
                <vs-button @click="addEditProductFamilyOption" class="mt-2">
                  {{ isEditProductFamily ? 'Update' : 'Add' }} Option
                </vs-button>
                <vs-button v-if="isEditProductFamily" @click="cancelEditProductFamilyHandler" type="border">Cancel</vs-button>
              </div>
              <vs-divider class="lg:hidden"></vs-divider>
              <div class="lg:col-span-2 lg:border lg:border-t-0 lg:border-r-0 lg:border-b-0 lg:border-solid d-theme-border-grey-light lg:pl-4">
                <h5 class="mb-3">Existing Product Family Options</h5>
                <div v-if="productFamilies.length > 0" class="max-h-xs overflow-y-auto">
                  <div
                    v-for="(productFamily, index) in productFamilies.filter(r => r._id !== 'other')"
                    :key="index"
                  >
                    <div class="flex justify-between items-center">
                      <span>
                        {{ productFamily.name }}
                      </span>
                      <div class="flex justify-center">
                        <div
                          v-if="currentlyDeletingProductFamilyId != productFamily._id"
                          class="flex gap-2"
                        >
                          <vx-tooltip text="Edit" delay=".5s">
                            <vs-button
                              @click="editProductFamilyHandler(productFamily)"
                              icon-pack="feather"
                              icon="icon-edit"
                              type="flat"
                            >
                            </vs-button>
                          </vx-tooltip>
                          <vx-tooltip
                            text="Delete"
                            delay=".5s"
                          >
                            <vs-button
                              @click="deleteProductFamilyHandler(productFamily)"
                              icon-pack="feather"
                              icon="icon-trash-2"
                              type="flat"
                            >
                            </vs-button>
                          </vx-tooltip>
                        </div>
                        <div
                          v-if="currentlyDeletingProductFamilyId === productFamily._id"
                          class="flex gap-2"
                          :key="'edit-' + productFamily._id"
                        >
                          <vx-tooltip
                            text="Confirm Delete?"
                            delay=".5s"
                          >
                            <vs-button
                              @click="confirmDeleteProductFamily(productFamily)"
                              icon-pack="feather"
                              icon="icon-check"
                              type="flat"
                              color="danger"
                            >
                            </vs-button>
                          </vx-tooltip>
                          <vx-tooltip
                            text="Cancel"
                            delay=".5s"
                          >
                            <vs-button
                              @click="currentlyDeletingProductFamilyId = ''"
                              icon-pack="feather"
                              icon="icon-x"
                              type="flat"
                            >
                            </vs-button>
                          </vx-tooltip>
                        </div>
                      </div>
                    </div>
                    <vs-divider class="my-1"></vs-divider>
                  </div>
                </div>
                <div v-else class="flex items-center justify-center h-32">
                  <p>No Product Family options.</p>
                </div>
              </div>
            </div>
          </div>
        </vs-popup>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import vSelect from 'vue-select';

export default {
  name: 'ProductFamilyManager',
  components: {
    vSelect,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localProductFamilyId: this.value,
      productFamilies: [],
      productFamilyPopupActivo: false,
      currentProductFamily: {
        name: '',
        id: '',
      },
      isEditProductFamily: false,
      currentlyDeletingProductFamilyId: '',
    };
  },
  watch: {
    value(newVal) {
      this.localProductFamilyId = newVal;
    },
  },
  methods: {
    ...mapActions('productFamily', [
      'fetchProductFamilies',
      'createProductFamily',
      'updateProductFamily',
      'deleteProductFamily',
    ]),
    
    handleProductFamilyChange(value) {
      this.localProductFamilyId = value;
      this.$emit('input', value);
    },

    async getProductFamilies() {
      try {
        const res = await this.fetchProductFamilies();
        this.productFamilies = res.data.data;
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to fetch product families',
          color: 'danger',
        });
      }
    },

    productFamilyPopupHandler() {
      this.productFamilyPopupActivo = true;
      this.getProductFamilies();
    },

    async addEditProductFamilyOption() {
      if (this.currentProductFamily.name && this.currentProductFamily.name.trim() !== '') {
        this.$vs.loading();

        try {
          if (!this.isEditProductFamily) {
            await this.createProductFamily({
              name: this.currentProductFamily.name,
            });
            this.$vs.notify({
              title: 'Success',
              text: 'Product family added successfully.',
              color: 'success',
            });
          } else {
            await this.updateProductFamily({
              name: this.currentProductFamily.name,
              _id: this.currentProductFamily.id,
            });
            this.$vs.notify({
              title: 'Success',
              text: 'Product family updated successfully.',
              color: 'success',
            });
          }
          
          await this.getProductFamilies();
          this.resetForm();
        } catch (err) {
          this.$vs.notify({
            title: 'Failed',
            text: `Failed to ${this.isEditProductFamily ? 'update' : 'add'} product family. Try again!`,
            color: 'danger',
          });
        } finally {
          this.$vs.loading.close();
        }
      } else {
        this.errors.add({
          field: 'productFamilytxtbox',
          msg: 'Product family name is required',
          scope: 'productFamily',
        });
      }
    },

    async editProductFamilyHandler(productFamily) {
      this.isEditProductFamily = true;
      this.currentProductFamily.name = productFamily.name;
      this.currentProductFamily.id = productFamily._id;
      this.currentlyDeletingProductFamilyId = '';
      
      this.$nextTick(() => {
        const inputRef = this.$refs.productFamilytxtbox;
        if (inputRef && inputRef.$el) {
          const inputElement = inputRef.$el.querySelector('input');
          if (inputElement) {
            inputElement.focus();
          }
        }
      });
    },

    cancelEditProductFamilyHandler() {
      this.resetForm();
    },

    deleteProductFamilyHandler(productFamily) {
      this.currentlyDeletingProductFamilyId = productFamily._id;
      this.isEditProductFamily = false;
      this.currentProductFamily.name = '';
      this.currentProductFamily.id = '';
      this.$validator.reset();
    },

    async confirmDeleteProductFamily(productFamily) {
      this.$vs.loading();

      try {
        await this.deleteProductFamily({
          _id: productFamily._id,
        });
        
        this.$vs.notify({
          title: 'Success',
          text: 'Product family deleted successfully.',
          color: 'success',
        });
        
        await this.getProductFamilies();
        this.resetForm();
      } catch (err) {
        this.$vs.notify({
          title: 'Failed',
          text: 'Failed to delete product family. Try again!',
          color: 'danger',
        });
      } finally {
        this.currentlyDeletingProductFamilyId = '';
        this.$vs.loading.close();
      }
    },

    resetForm() {
      this.isEditProductFamily = false;
      this.currentProductFamily.name = '';
      this.currentProductFamily.id = '';
      this.currentlyDeletingProductFamilyId = '';
      this.$validator.reset();
    },
  },

  async created() {
    await this.getProductFamilies();
  },
};
</script>

<style scoped>
.product-family-popup {
  z-index: 9999;
}
</style>